// src/store/redux/callSlice.ts
import { RootState } from '@/store';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// 定义呼叫记录的数据结构
export interface CallRecord {
    key: string;
    taskNo: string;
    calluuid: string;
    phone: string;
    count: number;
    successCount: number;
    failCount: number;
    answeredCount: number;
    noAnswerCount: number;
    status: 'idle' | 'calling' | 'ringing' | 'answered' | 'hangup' | boolean;
    answerStatus:string; // 添加 answerStatus 字段
    time: string;
    duration?: string;
    taskId?: string;
}

// 定义 slice 的状态结构
interface CallState {
    records: CallRecord[];
    currentCall: CallRecord | null;
    isCalling: boolean;
    callStatus: 'idle' | 'calling' | 'ringing' | 'answered' | 'hangup';
    answeredCount: number;
}

// 初始状态
const initialState: CallState = {
    records: [],
    currentCall: null,
    isCalling: false,
    callStatus: 'idle',
    answeredCount: 0,
};

// 创建 slice
export const callSlice = createSlice({
    name: 'call',
    initialState,
    reducers: {
        // 添加新的呼叫记录
        addCallRecord: (state, action: PayloadAction<CallRecord>) => {
            // 严格使用 taskNo 和 phone 组合检查是否已存在记录
            const existingIndex = state.records.findIndex(record =>
                record.taskNo === action.payload.taskNo &&
                record.phone === action.payload.phone
            );

            if (existingIndex === -1) {
                // 如果不存在，则添加新记录
                state.records.push(action.payload);
                state.currentCall = action.payload;
            } else {
                // 如果已存在，更新现有记录
                state.records[existingIndex] = {
                    ...state.records[existingIndex],
                    ...action.payload
                };
            }
        },


        // 更新呼叫记录
        updateCallRecord: (state, action: PayloadAction<CallRecord>) => {
            const index = state.records.findIndex(record => record.key === action.payload.key);
            if (index !== -1) {
                state.records[index] = action.payload;
            }
            if (state.currentCall && state.currentCall.key === action.payload.key) {
                state.currentCall = action.payload;
            }
        },

        /**
         * 更新通话记录状态的 reducer 函数。
         *
         * 根据传入的 key 或 taskNo + phone 组合查找并更新对应的通话记录状态，
         * 同时支持更新当前通话（currentCall）的状态。
         * 可选地增加计数、更新 answerStatus 和处理 answeredCount/noAnswerCount。
         *
         * @param state - 状态对象，包含 records 和 currentCall 等字段
         * @param action - Redux action，payload 包含以下字段：
         *   - key?: string;                  // 用于精确查找记录的唯一标识
         *   - taskNo: string;                // 任务编号，配合 phone 使用
         *   - calluuid: string;              // 通话 UUID
         *   - status: CallRecord['status'];  // 要更新的通话状态
         *   - phone?: string;                // 电话号码，配合 taskNo 使用
         *   - incrementCount?: boolean;      // 是否增加通话次数计数
         *   - answerStatus?: CallRecord['answerStatus']; // 回答状态（如 ring、answered）
         *   - answeredCount: number;         // 已接通次数
         *   - noAnswerCount: number;         // 未接通次数
         */
        updateCallStatus: (state, action: PayloadAction<{
            key?: string;
            taskNo: string;
            calluuid: string;
            status: CallRecord['status'];
            phone?: string;
            incrementCount?: boolean; // 添加增量计数选项
            answerStatus?: CallRecord['answerStatus']; // 添加 answerStatus 选项
            answeredCount: number;
            noAnswerCount: number;
        }>) => {
            console.log("updateCallStatus reducer 被调用");
            console.log("action payload:", action.payload);
            console.log("调用前的 state:", JSON.parse(JSON.stringify(state))); // 深拷贝避免 immer 问题

            const { key,taskNo, status, phone,incrementCount, answerStatus,answeredCount,noAnswerCount } = action.payload;

            if (key) {
                // 通过 key 查找并更新特定记录
                const record = state.records.find(record => record.key === key);
                console.log("通过 key 查找记录:", key, record);
                if (record) {
                    record.status = status;
                    const currentAnswerStatus = record.answerStatus;
                    if (incrementCount) {
                        record.count += 1;
                        console.log("计数增加到:", record.count);
                    }

                    // 处理 answeredCount 和 noAnswerCount 的更新逻辑
                    console.log("answeredCount!==undefined", answeredCount !== undefined);
                    if (answeredCount !== undefined) {
                        console.log("answeredCount==:", answeredCount);
                        console.log("currentAnswerStatus:", currentAnswerStatus);
                        // 判断之前是否是 ring 状态
                        if(currentAnswerStatus === "ring"){
                            record.answeredCount +=1;
                            record.noAnswerCount -=1;
                            console.log("answeredCount 更新为:", answeredCount);
                            console.log("noAnswerCount 减为:", noAnswerCount);
                        }

                    }else{
                        record.noAnswerCount += 1;
                        console.log("noAnswerCount 更新为:", noAnswerCount);
                    }

                    if (answerStatus !== undefined) {
                        record.answerStatus = answerStatus;
                        console.log("answerStatus 更新为:", answerStatus);
                    }
                }

                // 如果当前通话匹配 key，则同步更新 currentCall
                if (state.currentCall && state.currentCall.key === key) {
                    state.currentCall.status = status;
                    if (incrementCount) {
                        state.currentCall.count += 1;
                    }

                    // 处理 currentCall 中 answeredCount 和 noAnswerCount 的更新逻辑
                    console.log("answeredCount!==undefined", answeredCount !== undefined);
                    if (answeredCount !== undefined) {
                        console.log("answeredCount==:", answeredCount);
                        console.log("currentCall answerStatus:", state.currentCall.answerStatus);
                        state.currentCall.answeredCount = state.currentCall.answeredCount+1;
                        if(state.currentCall.answerStatus === "ring"){
                            state.currentCall.noAnswerCount = state.currentCall.noAnswerCount-1;
                            console.log("currentCall noAnswerCount 减为:", noAnswerCount);
                        }
                        console.log("currentCall answeredCount 更新为:", answeredCount);
                    }else{
                        state.currentCall.noAnswerCount = state.currentCall.noAnswerCount+1;
                        console.log("noAnswerCount 更新为:", noAnswerCount);
                    }
                    if (answerStatus !== undefined) {
                        state.currentCall.answerStatus = answerStatus;
                    }
                }
            } else if (taskNo && phone) {
                // 通过 taskNo 和 phone 组合查找并更新记录
                const record = state.records.find(record =>
                    record.taskNo === taskNo && record.phone === phone
                );
                console.log("通过 taskNo 和 phone 查找记录:", taskNo, phone, record);
                if (record) {
                    record.status = status;
                    if (incrementCount) {
                        record.count += 1;
                        console.log("计数增加到:", record.count);
                    }

                    // 处理 currentCall 中 answeredCount 和 noAnswerCount 的更新逻辑
                    if (state.currentCall && state.currentCall.answeredCount !== undefined) {
                        console.log("answeredCount==:", answeredCount);
                        console.log("currentCall answerStatus:", state.currentCall.answerStatus)
                        if (state.currentCall.answerStatus === "ring"){
                            state.currentCall.noAnswerCount = state.currentCall.noAnswerCount-1;
                            console.log("currentCall noAnswerCount 减为:", noAnswerCount);
                        }
                        state.currentCall.answeredCount = record.answeredCount+1;
                        console.log("currentCall answeredCount 更新为:", state.currentCall.answeredCount);
                    }else if(state.currentCall){
                        state.currentCall.noAnswerCount = record.noAnswerCount+1;
                    }

                    if (answerStatus !== undefined) {
                        record.answerStatus = answerStatus;
                        console.log("answerStatus 更新为:", answerStatus);
                    }
                } else {
                    console.log("未找到匹配的记录，创建新记录");
                    // 创建新记录
                    const newRecord: CallRecord = {
                        key: Date.now().toString(),
                        taskNo: taskNo,
                        calluuid: '',
                        phone: phone,
                        count: incrementCount ? 1 : 0,
                        successCount: 0,
                        failCount: 0,
                        answeredCount:0,
                        noAnswerCount:0,
                        status: status,
                        answerStatus: '',
                        time: new Date().toISOString()
                    };
                    state.records.push(newRecord);
                    console.log("创建新记录:", newRecord);
                }

                // 如果当前通话匹配 taskNo 和 phone，则同步更新 currentCall
                if (state.currentCall &&
                    state.currentCall.taskNo === taskNo &&
                    state.currentCall.phone === phone) {
                    state.currentCall.status = status;
                    if (incrementCount) {
                        state.currentCall.count += 1;
                    }
                    if (answerStatus !== undefined) {
                        state.currentCall.answerStatus = answerStatus;
                    }
                }
            }
            console.log("更新后的 state:", JSON.parse(JSON.stringify(state)));
        },


        // 增加呼叫次数
        incrementCallCount: (state, action: PayloadAction<string>) => {
            const record = state.records.find(record => record.key === action.payload);
            if (record) {
                record.count += 1;
            }
            if (state.currentCall && state.currentCall.key === action.payload) {
                state.currentCall.count += 1;
            }
        },

        // 设置当前呼叫状态
        setCurrentCallStatus: (state, action: PayloadAction<CallState['callStatus']>) => {
            state.callStatus = action.payload;
        },

        // 设置是否正在呼叫
        setIsCalling: (state, action: PayloadAction<boolean>) => {
            state.isCalling = action.payload;
        },

        // 设置当前呼叫
        setCurrentCall: (state, action: PayloadAction<CallRecord | null>) => {
            state.currentCall = action.payload;
        },

        // 清除所有呼叫记录
        clearCallRecords: (state) => {
            state.records = [];
            state.currentCall = null;
        },

        // 移除特定呼叫记录
        removeCallRecord: (state, action: PayloadAction<string>) => {
            state.records = state.records.filter(record => record.key !== action.payload);
            if (state.currentCall && state.currentCall.key === action.payload) {
                state.currentCall = null;
            }
        },
    },
});

// 导出 actions
export const {
    addCallRecord,
    updateCallRecord,
    updateCallStatus,
    incrementCallCount,
    setCurrentCallStatus,
    setIsCalling,
    setCurrentCall,
    clearCallRecords,
    removeCallRecord,
} = callSlice.actions;

// 导出 selector
// 如果 store 中使用的是 callSlice 作为键名
export const selectCallRecords = (state: RootState) => state.callSlice?.records || [];
export const selectCurrentCall = (state: RootState) => state.callSlice?.currentCall || null;
export const selectIsCalling = (state: RootState) => state.callSlice?.isCalling || false;
export const selectCallStatus = (state: RootState) => state.callSlice?.callStatus || 'idle';
// 默认导出 reducer
export default callSlice.reducer;
